import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { parseFeedQuery, serializeFeedQuery } from './feedViewUrl';

// The query, kept in the URL, so a read is shareable and survives a reload.
//
// This is the house direction — the two commits before this refactor began were "Open the Score
// Specs list on the reader's own specs" and "Keep the Score Specs list filters in the URL" — and the
// route already reserves the space: Router registers `${ROUTES.feedView}/*` and nothing has ever
// read it.
//
// A MIRROR, NOT THE STATE, and that is a deliberate departure from useListFilterUrl next door.
// There the URL IS the filter state, which works because every one of those filters is a scalar that
// round-trips exactly. Here one does not: a company chip carries a symbol and a name that came from
// a lookup, and the URL can only carry the id. Make the URL the source and the analyst watches their
// resolved chips degrade to bare `#123` the moment the query string is rewritten. So the reducer
// stays the source, this writes what it can, and hydration accepts the loss once — on arrival, where
// unverified ids are already an ordinary thing to be holding.
//
// Writes REPLACE rather than push: back should leave the Feed Viewer, not step back through every
// keystroke of a feed id.
export const useFeedViewUrlState = (formState, patchForm) => {
  const { search } = useLocation();
  const navigate = useNavigate();

  // Hydrate once, on mount, and only from a query string that says something. A patch rather than a
  // whole state, so an empty URL changes nothing — which is what keeps every existing test opening
  // on the form it expects rather than one the URL decided.
  const hydratedRef = React.useRef(false);
  const writtenRef = React.useRef(null);
  const patchInFlightRef = React.useRef(false);
  React.useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    // Seeded with what we ARRIVED holding, so a link whose query already matches the form it
    // produces never navigates at all.
    writtenRef.current = search.replace(/^\?/, '');
    const patch = parseFeedQuery(search);
    if (Object.keys(patch).length) {
      patchForm(patch);
      patchInFlightRef.current = true;
    }
    // Mount only. Re-running on `search` would fight the writer below, and re-running on formState
    // would re-apply the arrival URL over everything typed since.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // And write it back as the query changes. Guarded on the serialised STRING rather than on
  // formState, so a patch that changes nothing the URL carries — a mock-kind toggle, a company list
  // re-resolved to the same ids — does not navigate.
  React.useEffect(() => {
    // THE MOUNT COMMIT BELONGS TO THE URL, NOT TO US. Both effects run in the same commit and the
    // hydrate patch above has not landed yet, so serialising `formState` here would serialise the
    // PRE-hydration form — empty — and `navigate({ search: '' })` would destroy the query on the
    // current history entry. The address bar blanked on every shared link open, anything reading
    // `location.search` in that commit saw nothing, and the value only came back because the patch
    // forced one more render.
    if (patchInFlightRef.current) {
      patchInFlightRef.current = false;
      return;
    }
    const next = serializeFeedQuery(formState).toString();
    if (writtenRef.current === next) return;
    writtenRef.current = next;
    navigate({ search: next ? `?${next}` : '' }, { replace: true });
  }, [formState, navigate]);
};
